import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';

import { compSetsApi, type WeekRankBoat } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

function RankCell({
  rank,
  total,
  approxTotal = false,
}: {
  rank: number | null;
  total: number | null;
  // Boataround only stores your boats + competitors priced in front of them, so
  // its `total` is a floor (boats above your priciest aren't counted). Render it
  // as "N of M+" to signal the market is at least that big — the rank itself is
  // exact. MMK reads every comp-set boat, so its total is complete.
  approxTotal?: boolean;
}) {
  if (rank == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  const label = total != null ? `${rank} of ${total}${approxTotal ? '+' : ''}` : `Rank ${rank}`;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="font-medium tabular-nums">{rank}</span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function WeekRankTable({
  compSetId,
  weekStart,
  weekLabel,
}: {
  compSetId: string;
  weekStart: string;
  weekLabel: string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.compSets.weekRank(compSetId, weekStart),
    queryFn: () => compSetsApi.weekRank(compSetId, { weekStart }),
    enabled: !!weekStart,
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Week Rank · {weekLabel}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No ranked boats for this week.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Boat</TableHead>
                <TableHead className="text-right">MMK</TableHead>
                <TableHead className="text-right">Boataround</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row: WeekRankBoat) => (
                <TableRow key={row.boatPid}>
                  <TableCell className="text-sm font-medium">{row.boatName}</TableCell>
                  <TableCell className="text-right text-sm">
                    <RankCell rank={row.mmkRank} total={row.mmkTotal} />
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    <RankCell rank={row.boataroundRank} total={row.boataroundTotal} approxTotal />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
